/**
 * Which steps of the shared type scale this app actually uses.
 *
 * The scale comes from `@ayble-health/dashboard-components/theme` and is
 * therefore wider than careOS needs — it carries Chakra's full range up to
 * 128px and nine font weights. Rendering all of it in the Typography section
 * implies every step is a live choice, which makes the section a worse
 * reference: a designer scanning it cannot tell the four sizes the product
 * actually uses from the five that exist only because the package ships them.
 *
 * So the unused steps move to the Archive tab. They are **not** removed from
 * the theme — they are the package's tokens, not ours, and this repo pulls from
 * the package rather than editing it. This is a presentation split only.
 *
 * ## How this was measured
 *
 * Counted `fontSize` / `fontWeight` / `lineHeight` props across `src/`,
 * excluding `src/components/DesignSystem/` — the audit enumerates every token
 * by definition, so counting itself would make everything look used.
 *
 * Pinned literals, same standard as `ChangeBadge` and `parity.ts`: a usage
 * claim is a statement about a moment, so re-measure and re-pin rather than
 * letting it drift silently.
 */
export const TYPE_USAGE_MEASURED_ON = 'Aug 24, 2026';

/** Font-size steps with at least one call site outside the design system. */
export const USED_FONT_SIZES = [
  '2xs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
] as const;

/**
 * Display sizes from the package that nothing in careOS reaches for. 48px is
 * already larger than the biggest thing on any screen (the 36px greeting).
 */
export const UNUSED_FONT_SIZES = ['5xl', '6xl', '7xl', '8xl', '9xl'] as const;

/** Weights with at least one call site. `light` is used once, by the Text default. */
export const USED_FONT_WEIGHTS = [
  'light',
  'normal',
  'medium',
  'semibold',
  'bold',
] as const;

/** Chakra's extremes. CircularXX ships none of these cuts, so they would synthesise. */
export const UNUSED_FONT_WEIGHTS = [
  'hairline',
  'thin',
  'extrabold',
  'black',
  'extraBlack',
] as const;

/**
 * Every line-height token is unused, which is worth stating plainly rather than
 * leaving as an absence: the shared `Text` defaults to `lineHeight="base"` and
 * `Heading` to `"shorter"`, and **neither of those names exists in
 * `lineHeightTokens`** — they resolve against Chakra's own defaults instead. The
 * careOS surfaces set line height through the CSS custom properties in
 * `careos-prototype.css`. So this token group is currently decorative.
 *
 * Worth raising upstream: either the shared components should default to tokens
 * from the set the package ships, or the set should go.
 */
export const LINE_HEIGHTS_ALL_UNUSED = true;
